import { Interface, formatUnits, parseEther } from "ethers"
import { getChainProvider } from "@/lib/chainProvider"
import { MulticallService } from "@/services/multicallService"
import type { DirectoryEntry } from "@/components/StreamCard"

export const GAME_CHAIN_ID = Number(import.meta.env.VITE_CHAIN_ID || 56)
export const VANITY_REGISTRY_ADDRESS = import.meta.env.VITE_VANITY_REGISTRY_ADDRESS
export const STREAM_VAULT_ADDRESS = import.meta.env.VITE_STREAM_VAULT_ADDRESS

export const REGISTRY_ABI = [
  "function getCategoryCount(uint8 category) external view returns (uint256)",
  "function getNamesByCategory(uint8 category, uint256 cursor, uint256 limit) external view returns (string[] names, address[] owners, uint256 nextCursor)",
  "function getNamesByCategoryFiltered(uint8 category, uint256 cursor, uint256 limit, bool verifiedOnly, uint256 threshold) external view returns (string[] names, address[] owners, uint256 nextCursor)",
  "function getProfile(string name) external view returns (tuple(string bio, string location, string website, string displayName, string profilePic, string coverImage, string pageTitle, string message, string videoUrl))",
  "function getVerifiedCountByCategory(uint8 category) external view returns (uint256)",
  "function isVerified(string name) external view returns (bool)",
  "function setVerified(string name, bool status) external",
  "function EDITOR_ROLE() external view returns (bytes32)",
  "function SUPERVISOR_ROLE() external view returns (bytes32)",
  "function hasRole(bytes32 role, address account) external view returns (bool)",
  "function getLatestNames(uint256 cursor, uint256 limit, uint256 threshold) external view returns (string[] names, address[] owners, uint256 nextCursor)",
  "function getLatestStreams(uint256 cursor, uint256 limit, uint256 threshold) external view returns (string[] names, address[] owners, uint8[] categories, string[] content, uint256 nextCursor)",
  "function getLatestCount() external view returns (uint256)",
  "function tagRegistry() external view returns (address)",
]

export const TAG_REGISTRY_ABI = [
  "function getTopTags(uint256 n) external view returns (string[] tags, uint256[] counts)",
  "function getTopTagsPaged(uint256 offset, uint256 limit) external view returns (string[] tags, uint256[] counts)",
  "function getNamesByTag(string tag, uint256 cursor, uint256 limit) external view returns (string[] names, address[] owners, uint256 nextCursor)",
  "function getTagCount(string tag) external view returns (uint256)",
  "function getTagHeapSize() external view returns (uint256)",
  "function getTagsByName(string name) external view returns (string[])",
]

export const VAULT_ABI = [
  "function lifetimeReceived(address) external view returns (uint256)",
]

export const registryIface = new Interface(REGISTRY_ABI)
export const tagRegistryIface = new Interface(TAG_REGISTRY_ABI)
export const vaultIface = new Interface(VAULT_ABI)

// Cached TagRegistry address (resolved once from VanityRegistry.tagRegistry())
let _tagRegistryAddress: string | null = null

export async function getTagRegistryAddress(): Promise<string | null> {
  if (_tagRegistryAddress) return _tagRegistryAddress
  if (!VANITY_REGISTRY_ADDRESS) return null
  try {
    const provider = await getChainProvider(GAME_CHAIN_ID)
    const { Contract } = await import("ethers")
    const vr = new Contract(VANITY_REGISTRY_ADDRESS, REGISTRY_ABI, provider)
    const addr = await vr.tagRegistry() as string
    if (addr && addr !== "0x0000000000000000000000000000000000000000") {
      _tagRegistryAddress = addr
    }
    return _tagRegistryAddress
  } catch {
    return null
  }
}

export async function fetchCategoryEntries(
  multicall: MulticallService,
  categoryId: number,
  cursor: bigint,
  limit: number
): Promise<DirectoryEntry[]> {
  if (!VANITY_REGISTRY_ADDRESS) return []

  // Step 1: Get names + owners from category
  const provider = await getChainProvider(GAME_CHAIN_ID)
  const { Contract } = await import("ethers")
  const registryContract = new Contract(VANITY_REGISTRY_ADDRESS, REGISTRY_ABI, provider)
  const [names, owners] = await registryContract.getNamesByCategory(categoryId, cursor, limit) as [string[], string[]]

  if (names.length === 0) return []

  // Step 2: Batch fetch profiles + deposited BNB via multicall
  const profileCalls = names.map((name: string) =>
    multicall.encodeCall(VANITY_REGISTRY_ADDRESS, registryIface, "getProfile", [name])
  )

  const depositCalls = STREAM_VAULT_ADDRESS
    ? owners.map((owner: string) =>
        multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, "lifetimeReceived", [owner])
      )
    : []

  const allCalls = [...profileCalls, ...depositCalls]
  const results = await multicall.callAndDecode<unknown[]>(allCalls)

  const entries: DirectoryEntry[] = []
  for (let i = 0; i < names.length; i++) {
    const profile = results[i] as {
      bio: string; location: string; website: string; displayName: string;
      profilePic: string; coverImage: string; pageTitle: string; message: string
    }

    // Frontend filter: skip incomplete profiles
    if (!profile.displayName && !profile.coverImage) continue

    const raised = STREAM_VAULT_ADDRESS
      ? formatBnb(results[names.length + i] as bigint)
      : "0"

    entries.push({
      name: names[i],
      owner: owners[i],
      displayName: profile.displayName || names[i],
      location: profile.location || "",
      coverImage: profile.coverImage || "",
      profilePic: profile.profilePic || "",
      raised,
    })
  }

  return entries
}

export async function fetchCategoryEntriesFiltered(
  multicall: MulticallService,
  categoryId: number,
  cursor: bigint,
  limit: number,
  verifiedOnly: boolean,
  threshold: bigint = parseEther("0.25")
): Promise<{ entries: DirectoryEntry[]; nextCursor: bigint }> {
  if (!VANITY_REGISTRY_ADDRESS) return { entries: [], nextCursor: 0n }

  const provider = await getChainProvider(GAME_CHAIN_ID)
  const { Contract } = await import("ethers")
  const registryContract = new Contract(VANITY_REGISTRY_ADDRESS, REGISTRY_ABI, provider)
  const [names, owners, rawNextCursor] = await registryContract.getNamesByCategoryFiltered(categoryId, cursor, limit, verifiedOnly, threshold) as [string[], string[], bigint]

  if (names.length === 0) return { entries: [], nextCursor: rawNextCursor }

  const profileCalls = names.map((name: string) =>
    multicall.encodeCall(VANITY_REGISTRY_ADDRESS, registryIface, "getProfile", [name])
  )

  const depositCalls = STREAM_VAULT_ADDRESS
    ? owners.map((owner: string) =>
        multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, "lifetimeReceived", [owner])
      )
    : []

  const allCalls = [...profileCalls, ...depositCalls]
  const results = await multicall.callAndDecode<unknown[]>(allCalls)

  const entries: DirectoryEntry[] = []
  for (let i = 0; i < names.length; i++) {
    const profile = results[i] as {
      bio: string; location: string; website: string; displayName: string;
      profilePic: string; coverImage: string; pageTitle: string; message: string
    }

    if (!profile.displayName && !profile.coverImage) continue

    const raised = STREAM_VAULT_ADDRESS
      ? formatBnb(results[names.length + i] as bigint)
      : "0"

    entries.push({
      name: names[i],
      owner: owners[i],
      displayName: profile.displayName || names[i],
      location: profile.location || "",
      coverImage: profile.coverImage || "",
      profilePic: profile.profilePic || "",
      raised,
    })
  }

  return { entries, nextCursor: rawNextCursor }
}

export function formatBnb(value: bigint): string {
  const f = parseFloat(formatUnits(value, 18))
  if (f === 0) return "0"
  if (f < 0.001) return "<0.001"
  return f.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })
}

export async function fetchLatestEntries(
  multicall: MulticallService,
  cursor: bigint,
  limit: number,
  threshold: bigint = parseEther("0.25")
): Promise<{ entries: DirectoryEntry[]; nextCursor: bigint }> {
  if (!VANITY_REGISTRY_ADDRESS) return { entries: [], nextCursor: 0n }

  const provider = await getChainProvider(GAME_CHAIN_ID)
  const { Contract } = await import("ethers")
  const registryContract = new Contract(VANITY_REGISTRY_ADDRESS, REGISTRY_ABI, provider)
  const [names, owners, rawNextCursor] = await registryContract.getLatestNames(cursor, limit, threshold) as [string[], string[], bigint]

  if (names.length === 0) return { entries: [], nextCursor: rawNextCursor }

  const profileCalls = names.map((name: string) =>
    multicall.encodeCall(VANITY_REGISTRY_ADDRESS, registryIface, "getProfile", [name])
  )
  const depositCalls = STREAM_VAULT_ADDRESS
    ? owners.map((owner: string) =>
        multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, "lifetimeReceived", [owner])
      )
    : []

  const results = await multicall.callAndDecode<unknown[]>([...profileCalls, ...depositCalls])

  const entries: DirectoryEntry[] = []
  const seen = new Set<string>()
  for (let i = 0; i < names.length; i++) {
    const profile = results[i] as {
      bio: string; location: string; website: string; displayName: string;
      profilePic: string; coverImage: string; pageTitle: string; message: string
    }
    if (!profile.displayName && !profile.coverImage) continue
    if (seen.has(names[i])) continue
    seen.add(names[i])
    const raised = STREAM_VAULT_ADDRESS ? formatBnb(results[names.length + i] as bigint) : "0"
    entries.push({
      name: names[i],
      owner: owners[i],
      displayName: profile.displayName || names[i],
      location: profile.location || "",
      coverImage: profile.coverImage || "",
      profilePic: profile.profilePic || "",
      raised,
    })
  }

  return { entries, nextCursor: rawNextCursor }
}

export async function fetchTagEntries(
  multicall: MulticallService,
  tag: string,
  cursor: bigint,
  limit: number,
): Promise<{ entries: DirectoryEntry[]; nextCursor: bigint }> {
  const tagAddr = await getTagRegistryAddress()
  if (!tagAddr) return { entries: [], nextCursor: 0n }

  const provider = await getChainProvider(GAME_CHAIN_ID)
  const { Contract } = await import("ethers")
  const tagContract = new Contract(tagAddr, TAG_REGISTRY_ABI, provider)
  const [names, owners, rawNextCursor] = await tagContract.getNamesByTag(tag, cursor, limit) as [string[], string[], bigint]

  if (names.length === 0) return { entries: [], nextCursor: rawNextCursor }

  const profileCalls = names.map((name: string) =>
    multicall.encodeCall(VANITY_REGISTRY_ADDRESS, registryIface, "getProfile", [name])
  )
  const depositCalls = STREAM_VAULT_ADDRESS
    ? owners.map((owner: string) =>
        multicall.encodeCall(STREAM_VAULT_ADDRESS, vaultIface, "lifetimeReceived", [owner])
      )
    : []

  const results = await multicall.callAndDecode<unknown[]>([...profileCalls, ...depositCalls])

  const entries: DirectoryEntry[] = []
  const seen = new Set<string>()
  for (let i = 0; i < names.length; i++) {
    const profile = results[i] as {
      bio: string; location: string; website: string; displayName: string;
      profilePic: string; coverImage: string; pageTitle: string; message: string
    }
    if (!profile.displayName && !profile.coverImage) continue
    if (seen.has(names[i])) continue
    seen.add(names[i])
    const raised = STREAM_VAULT_ADDRESS ? formatBnb(results[names.length + i] as bigint) : "0"
    entries.push({
      name: names[i],
      owner: owners[i],
      displayName: profile.displayName || names[i],
      location: profile.location || "",
      coverImage: profile.coverImage || "",
      profilePic: profile.profilePic || "",
      raised,
    })
  }

  return { entries, nextCursor: rawNextCursor }
}
